import React, { useState, useEffect } from 'react';
import { useFoodSafetyContext, FS_SCREENS } from '../context/FoodSafetyContext';
import type { FSSessionData } from '../context/FoodSafetyContext';
import { foodSafetyService } from '../services/foodSafetyService';
import moment from 'moment';

const WelcomeScreen: React.FC = () => {
    const { dispatch } = useFoodSafetyContext();
    const [foodSafetyType, setFoodSafetyType] = useState('');
    const [shift, setShift] = useState('');
    const [pausedTours, setPausedTours] = useState<any[]>([]);

    useEffect(() => {
        const fetchPausedTours = async () => {
            try {
                const tours = await foodSafetyService.getTourHistory();
                const paused = tours.filter((t: any) => t.cr3ea_status === 'InProgress-paused');
                setPausedTours(paused);
            } catch (err) {
                console.error("Failed to fetch paused tours", err);
            }
        };
        fetchPausedTours();
    }, []);

    const handleContinue = () => {
        if (!foodSafetyType || !shift) {
            alert('Please select both Food Safety Checklist type and Shift to continue.');
            return;
        }
        
        dispatch({
            type: 'SET_SELECTIONS',
            payload: { type: foodSafetyType, shift }
        });
        
        dispatch({
            type: 'SET_SCREEN',
            payload: FS_SCREENS.CHECKLIST_INFO
        });
    };

    const handleResume = (tour: any) => {
        const sessionData: FSSessionData = {
            site: tour.cr3ea_plantid || '',
            line: tour.cr3ea_lineno || '',
            qaExecutive: tour.cr3ea_assigned_qa || '',
            productionIncharge: tour.cr3ea_shiftexecutiveproduction || '',
            areaIncharge: tour.cr3ea_food_safety_areaincharge || '',
            cycle: tour.cr3ea_food_safety_cycle || '',
            tourStartDate: tour.cr3ea_tourstartdate || new Date().toISOString()
        };

        dispatch({
            type: 'SET_SELECTIONS',
            payload: { type: tour.cr3ea_food_safety_checklisttype, shift: tour.cr3ea_shift || 'Shift 1' }
        });
        
        dispatch({ type: 'SET_TOUR_ID', payload: tour.cr3ea_prod_rajpura_quality_tourid });
        dispatch({ type: 'SET_SESSION_DATA', payload: sessionData });
        dispatch({ type: 'SET_IS_PAUSED', payload: true });

        // Navigate to appropriate screen
        if (tour.cr3ea_food_safety_checklisttype === "PPE Checklist") {
            dispatch({ type: 'SET_SCREEN', payload: FS_SCREENS.PPE_CHECKLIST });
        } else if (tour.cr3ea_food_safety_checklisttype === "GMP Checklist") {
            dispatch({ type: 'SET_SCREEN', payload: FS_SCREENS.GMP_CHECKLIST });
        } else if (tour.cr3ea_food_safety_checklisttype === "PCI Checklist") {
            dispatch({ type: 'SET_SCREEN', payload: FS_SCREENS.PCI_CHECKLIST });
        }
    };

    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-bold mb-0">Food Safety Inspections</h4>
                <button 
                    className="btn btn-outline-primary"
                    onClick={() => dispatch({ type: 'SET_SCREEN', payload: FS_SCREENS.DASHBOARD })}
                >
                    <i className="bi bi-graph-up me-2"></i>Analytics Dashboard
                </button>
            </div>

            <div className="card shadow-sm border-0 mb-4">
                <div className="card-header bg-white py-3 border-bottom-0">
                    <h5 className="card-title mb-0 fw-bold">Start New Checklist</h5>
                </div>
                <div className="card-body p-4">
                    <div className="row g-4">
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">Food Safety Checklist <span className="text-danger">*</span></label>
                            <select 
                                className="form-select" 
                                value={foodSafetyType} 
                                onChange={(e) => setFoodSafetyType(e.target.value)}
                            >
                                <option value="">Select Checklist Type</option>
                                <option value="PPE Checklist">PPE Checklist</option>
                                <option value="GMP Checklist">GMP Checklist</option>
                                <option value="PCI Checklist">PCI Checklist</option>
                            </select>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">Shift <span className="text-danger">*</span></label>
                            <select 
                                className="form-select" 
                                value={shift} 
                                onChange={(e) => setShift(e.target.value)}
                            >
                                <option value="">Select Shift</option>
                                <option value="Shift 1">Shift 1</option>
                                <option value="Shift 2">Shift 2</option>
                                <option value="Shift 3">Shift 3</option>
                            </select>
                        </div>
                    </div>

                    <div className="d-flex justify-content-end mt-5">
                        <button 
                            className="btn btn-primary px-4" 
                            onClick={handleContinue}
                            disabled={!foodSafetyType || !shift}
                        >
                            Continue <i className="bi bi-arrow-right ms-2"></i>
                        </button>
                    </div>
                </div>
            </div>

            {pausedTours.length > 0 && (
                <div className="card shadow-sm border-0 mb-4">
                    <div className="card-header bg-white py-3 border-bottom-0 d-flex justify-content-between align-items-center">
                        <h5 className="card-title mb-0 fw-bold text-warning">
                            <i className="bi bi-pause-circle me-2"></i>Paused Audits
                        </h5>
                    </div>
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover mb-0 align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th className="px-4">Date</th>
                                        <th>Checklist Type</th>
                                        <th>Site / Line</th>
                                        <th>Shift</th>
                                        <th className="text-end px-4">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pausedTours.map((tour) => (
                                        <tr key={tour.cr3ea_prod_rajpura_quality_tourid}>
                                            <td className="px-4">
                                                {tour.cr3ea_tourstartdate ? moment(tour.cr3ea_tourstartdate).format("DD-MM-YYYY hh:mm A") : "N/A"}
                                            </td>
                                            <td className="fw-semibold text-primary">{tour.cr3ea_food_safety_checklisttype}</td>
                                            <td>{tour.cr3ea_plantid} / {tour.cr3ea_lineno}</td>
                                            <td>{tour.cr3ea_shift}</td>
                                            <td className="text-end px-4">
                                                <button 
                                                    className="btn btn-sm btn-outline-warning fw-bold"
                                                    onClick={() => handleResume(tour)}
                                                >
                                                    Resume <i className="bi bi-play-fill ms-1"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WelcomeScreen;
